import React, { useState, useEffect } from 'react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { deleteItemFromCartAsync } from '../../cart/cartSlice';

import { API_URL } from '../../../app/apiConfig';

const FALLBACK_CLIENT_ID =
  process.env.REACT_APP_PAYPAL_CLIENT_ID &&
  process.env.REACT_APP_PAYPAL_CLIENT_ID !== 'test' &&
  process.env.REACT_APP_PAYPAL_CLIENT_ID !== 'test_client_id' &&
  process.env.REACT_APP_PAYPAL_CLIENT_ID !== 'mock'
    ? process.env.REACT_APP_PAYPAL_CLIENT_ID.trim()
    : null;

export default function PayPalCheckoutButton({
  items,
  totalAmount,
  totalItems,
  selectedAddress,
  subscriptionType,
  checkoutEmail,
  disabled = false,
  onValidate,
  getOrderAddress,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  const [activeClientId, setActiveClientId] = useState(FALLBACK_CLIENT_ID);
  const [activeCurrency, setActiveCurrency] = useState('EUR');

  useEffect(() => {
    let isMounted = true;
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;

    fetch(`${API_URL}/api/orders/paypal-config`, {
      credentials: 'include',
      headers,
    })
      .then((res) => {
        if (!res.ok) throw new Error('Could not fetch PayPal config');
        return res.json();
      })
      .then((data) => {
        if (!isMounted) return;
        if (data && data.clientId) {
          const trimmed = data.clientId.trim();
          if (trimmed && trimmed !== 'test' && trimmed !== 'test_client_id' && trimmed !== 'mock') {
            setActiveClientId(trimmed);
          }
        }
        if (data && data.currency) {
          setActiveCurrency(data.currency.trim());
        }
      })
      .catch((err) => {
        console.warn('Could not load PayPal config from server:', err);
      })
      .finally(() => {
        if (isMounted) setIsLoadingConfig(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleCreateOrder = async () => {
    setErrorMessage(null);
    if (onValidate) {
      const isValid = onValidate();
      if (!isValid) {
        throw new Error('Please fill in required checkout details before paying.');
      }
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/api/orders/paypal-create`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          items,
          totalAmount,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.id) {
        throw new Error(data.error || 'Failed to initialize PayPal transaction');
      }

      return data.id;
    } catch (err) {
      console.error('PayPal createOrder error:', err);
      setErrorMessage(err.message || 'Could not connect to PayPal. Please try again.');
      throw err;
    }
  };

  const handleApprove = async (data, actions) => {
    setIsProcessing(true);
    setErrorMessage(null);

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const effectiveAddress = getOrderAddress ? getOrderAddress() : selectedAddress;

      const orderData = {
        items,
        totalAmount,
        totalItems,
        selectedAddress: effectiveAddress,
        subscriptionType,
        checkoutEmail: checkoutEmail || '',
      };

      const response = await fetch(`${API_URL}/api/orders/paypal-capture`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          orderID: data.orderID,
          orderData,
        }),
      });

      const createdOrder = await response.json();
      if (!response.ok || !createdOrder.id) {
        throw new Error(createdOrder.error || 'Payment capture failed');
      }

      // Clear all items from cart in background
      for (const item of items) {
        dispatch(deleteItemFromCartAsync(item.id));
      }

      // Navigate to order success page
      navigate(`/order-success/${createdOrder.id}`);
    } catch (err) {
      console.error('PayPal onApprove error:', err);
      setErrorMessage(err.message || 'Payment capture failed. Please contact support.');
      setIsProcessing(false);
    }
  };

  const isLocalDev =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  const hasRealClientId =
    Boolean(activeClientId) &&
    activeClientId !== 'test' &&
    activeClientId !== 'test_client_id' &&
    activeClientId !== 'mock';

  const handleMockCheckout = async () => {
    if (disabled || isProcessing) return;
    setErrorMessage(null);

    if (onValidate) {
      const isValid = onValidate();
      if (!isValid) return;
    }

    setIsProcessing(true);

    try {
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      // 1. Create simulated order on backend
      const createRes = await fetch(`${API_URL}/api/orders/paypal-create`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ items, totalAmount }),
      });
      const createData = await createRes.json();
      if (!createRes.ok || !createData.id) {
        throw new Error(createData.error || 'Failed to initialize simulated PayPal transaction');
      }

      // Small delay for natural UX feel
      await new Promise((res) => setTimeout(res, 600));

      // 2. Capture simulated order
      const effectiveAddress = getOrderAddress ? getOrderAddress() : selectedAddress;
      const orderData = {
        items,
        totalAmount,
        totalItems,
        selectedAddress: effectiveAddress,
        subscriptionType,
        checkoutEmail: checkoutEmail || '',
      };

      const captureRes = await fetch(`${API_URL}/api/orders/paypal-capture`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          orderID: createData.id,
          orderData,
        }),
      });

      const createdOrder = await captureRes.json();
      if (!captureRes.ok || !createdOrder.id) {
        throw new Error(createdOrder.error || 'Simulated payment capture failed');
      }

      // 3. Clear items from cart
      for (const item of items) {
        dispatch(deleteItemFromCartAsync(item.id));
      }

      // 4. Navigate to order confirmation
      navigate(`/order-success/${createdOrder.id}`);
    } catch (err) {
      console.error('Mock PayPal checkout error:', err);
      setErrorMessage(err.message || 'Payment simulation failed. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="w-full">
      {errorMessage && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl flex items-center justify-between">
          <span>{errorMessage}</span>
          <button
            type="button"
            onClick={() => setErrorMessage(null)}
            className="text-red-500 font-bold ml-2 hover:text-red-700"
          >
            ×
          </button>
        </div>
      )}

      {isProcessing && (
        <div className="mb-4 p-4 bg-sky-50 border border-sky-200 rounded-xl text-center">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-sky-500 border-t-transparent mb-2"></div>
          <p className="text-xs font-semibold text-sky-800">
            Confirming your PayPal payment & placing your order...
          </p>
        </div>
      )}

      {isLoadingConfig ? (
        <div className="w-full py-5 px-6 bg-slate-50 border border-slate-200/90 rounded-xl text-center flex flex-col items-center justify-center gap-2.5">
          <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-amber-500 border-t-transparent"></div>
          <span className="text-xs font-medium text-slate-600 tracking-wide">
            Connecting securely to PayPal...
          </span>
        </div>
      ) : hasRealClientId ? (
        <PayPalScriptProvider
          options={{
            clientId: activeClientId,
            currency: activeCurrency || 'EUR',
            intent: 'capture',
          }}
          key={`${activeClientId}-${activeCurrency}`}
        >
          <div className={`transition-opacity duration-200 ${disabled || isProcessing ? 'opacity-50 pointer-events-none' : ''}`}>
            <PayPalButtons
              style={{
                layout: 'vertical',
                color: 'gold',
                shape: 'rect',
                label: 'paypal',
                height: 48,
              }}
              disabled={disabled || isProcessing}
              createOrder={handleCreateOrder}
              onApprove={handleApprove}
              onError={(err) => {
                console.error('PayPal Buttons error:', err);
                setErrorMessage('PayPal encountered an error. Please try again or use another payment method.');
              }}
              onCancel={() => {
                setErrorMessage('Payment cancelled. You can retry whenever you are ready.');
              }}
            />
          </div>
        </PayPalScriptProvider>
      ) : isLocalDev ? (
        <div className={`space-y-2.5 transition-opacity duration-200 ${disabled || isProcessing ? 'opacity-50 pointer-events-none' : ''}`}>
          {/* Yellow PayPal Button (Localhost dev only) */}
          <button
            type="button"
            onClick={handleMockCheckout}
            disabled={disabled || isProcessing}
            className="w-full h-12 bg-[#FFC439] hover:bg-[#F4BB30] text-[#003087] font-bold rounded-md shadow-xs flex items-center justify-center gap-1.5 transition-all text-sm tracking-wide"
          >
            <span className="italic font-extrabold text-base tracking-tighter">Pay<span className="text-[#0079C1]">Pal</span></span>
            <span className="text-[11px] font-semibold text-blue-900 bg-white/60 px-2 py-0.5 rounded-full ml-1">
              Test Payment (Local)
            </span>
          </button>

          {/* Black Debit or Credit Card Button */}
          <button
            type="button"
            onClick={handleMockCheckout}
            disabled={disabled || isProcessing}
            className="w-full h-12 bg-[#2C2E2F] hover:bg-black text-white font-medium rounded-md shadow-xs flex items-center justify-center gap-2 transition-all text-sm"
          >
            <svg className="w-4 h-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            <span>Debit or Credit Card (Local Test)</span>
          </button>

          <p className="text-[11px] text-center text-slate-500 pt-1">
            🧪 <strong>Localhost Test Mode</strong>: Simulates instant payment without a real PayPal account.
          </p>
        </div>
      ) : (
        <div className="w-full p-4 bg-amber-50 border border-amber-200 rounded-xl text-center text-xs text-amber-800">
          <p className="font-semibold mb-1">PayPal is currently undergoing maintenance</p>
          <p className="text-[11px] text-amber-700">Please choose Manual Payment or contact support to proceed with your subscription.</p>
        </div>
      )}
    </div>
  );
}
